import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Package, Download, RefreshCw, Plus, Trash2, Link2, Save, Check, ClipboardCheck } from 'lucide-react';
import theme from '../theme';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NftProjectDetail = ({ user }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [genError, setGenError] = useState('');

  // IPFS state
  const [ipfsForm, setIpfsForm] = useState({ ipfs_cid: '', folder_path: 'metadata' });
  const [ipfsSaving, setIpfsSaving] = useState(false);
  const [ipfsError, setIpfsError] = useState('');
  const [ipfsSaved, setIpfsSaved] = useState(false);

  // Mint status state
  const [mintForm, setMintForm] = useState({
    mint_status: 'NOT_MINTED', minted_count: 0,
    recipient_address: '', batch_tx_hash: '', notes: ''
  });
  const [mintSaving, setMintSaving] = useState(false);
  const [mintError, setMintError] = useState('');
  const [mintSaved, setMintSaved] = useState(false);

  // Generate form state
  const [genForm, setGenForm] = useState({
    quantity: 10,
    base_name: '',
    description: '',
    image_base_url: '',
    attributes_schema: []
  });

  useEffect(() => { fetchProject(); }, [id]);

  const fetchProject = async () => {
    try {
      const res = await axios.get(`${API}/nft/projects/${id}`);
      const proj = res.data.project;
      setProject(proj);
      setItems(res.data.items || []);
      setGenForm(prev => ({
        ...prev,
        quantity: proj.quantity || 10,
        base_name: proj.base_name || '',
        description: proj.description || ''
      }));
      setIpfsForm({
        ipfs_cid: proj.ipfs_cid || '',
        folder_path: proj.folder_path || 'metadata'
      });
      setMintForm({
        mint_status: proj.mint_status || 'NOT_MINTED',
        minted_count: proj.minted_count || 0,
        recipient_address: proj.recipient_address || '',
        batch_tx_hash: proj.batch_tx_hash || '',
        notes: proj.mint_notes || ''
      });
    } catch (err) {
      console.error('Error loading project:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddAttribute = () => {
    setGenForm(prev => ({
      ...prev,
      attributes_schema: [...prev.attributes_schema, { trait_type: '', values: '' }]
    }));
  };

  const handleRemoveAttribute = (idx) => {
    setGenForm(prev => ({
      ...prev,
      attributes_schema: prev.attributes_schema.filter((_, i) => i !== idx)
    }));
  };

  const handleAttrChange = (idx, field, value) => {
    setGenForm(prev => {
      const updated = [...prev.attributes_schema];
      updated[idx] = { ...updated[idx], [field]: value };
      return { ...prev, attributes_schema: updated };
    });
  };

  const handleGenerate = async () => {
    setGenError('');
    setGenerating(true);
    try {
      const payload = {
        quantity: parseInt(genForm.quantity, 10),
        base_name: genForm.base_name,
        description: genForm.description,
        image_base_url: genForm.image_base_url,
        attributes_schema: genForm.attributes_schema.map(a => ({
          trait_type: a.trait_type,
          values: a.values ? a.values.split(',').map(v => v.trim()).filter(Boolean) : []
        }))
      };
      await axios.post(`${API}/nft/projects/${id}/items/generate`, payload);
      await fetchProject();
    } catch (err) {
      setGenError(err.response?.data?.detail || 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/nft/projects/${id}/export`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${project?.name || 'nft'}_metadata_pack.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
      alert('Export failed. Generate items first.');
    } finally {
      setExporting(false);
    }
  };

  const handleSaveIpfs = async () => {
    setIpfsError('');
    setIpfsSaving(true);
    setIpfsSaved(false);
    try {
      const res = await axios.patch(`${API}/nft/projects/${id}/ipfs`, {
        ipfs_cid: ipfsForm.ipfs_cid,
        folder_path: ipfsForm.folder_path
      });
      if (res.data.success) {
        setProject(prev => ({ ...prev, ipfs_cid: res.data.ipfs_cid, folder_path: res.data.folder_path, base_uri: res.data.base_uri }));
        setIpfsSaved(true);
        setTimeout(() => setIpfsSaved(false), 3000);
      }
    } catch (err) {
      setIpfsError(err.response?.data?.detail || 'Failed to save IPFS CID');
    } finally {
      setIpfsSaving(false);
    }
  };

  const computedBaseUri = ipfsForm.ipfs_cid
    ? `ipfs://${ipfsForm.ipfs_cid.replace(/^ipfs:\/\//, '').replace(/\/+$/, '')}/${(ipfsForm.folder_path || 'metadata').replace(/^\/+|\/+$/g, '')}/`
    : '';

  const handleSaveMintStatus = async () => {
    setMintError('');
    setMintSaving(true);
    setMintSaved(false);
    try {
      const res = await axios.patch(`${API}/nft/projects/${id}/mint-status`, {
        mint_status: mintForm.mint_status,
        minted_count: parseInt(mintForm.minted_count, 10) || 0,
        recipient_address: mintForm.recipient_address,
        batch_tx_hash: mintForm.batch_tx_hash,
        notes: mintForm.notes
      });
      if (res.data.success) {
        setProject(prev => ({
          ...prev,
          mint_status: res.data.mint_status,
          minted_count: res.data.minted_count,
          recipient_address: res.data.recipient_address,
          batch_tx_hash: res.data.batch_tx_hash,
          mint_notes: res.data.mint_notes,
          mint_confirmed_at: res.data.mint_confirmed_at
        }));
        setMintSaved(true);
        setTimeout(() => setMintSaved(false), 3000);
      }
    } catch (err) {
      setMintError(err.response?.data?.detail || 'Failed to save mint status');
    } finally {
      setMintSaving(false);
    }
  };

  const MINT_STATUS_COLORS = {
    NOT_MINTED: theme.colors.text.muted,
    PARTIAL: theme.colors.status.warning || '#f59e0b',
    MINTED: theme.colors.status.success
  };

  if (loading) {
    return <div style={{ padding: '40px', textAlign: 'center', color: theme.colors.text.secondary }}>Loading project...</div>;
  }

  if (!project) {
    return <div style={{ padding: '40px', textAlign: 'center', color: theme.colors.status.error }}>Project not found</div>;
  }

  const stdColor = project.standard === 'ERC1155' ? theme.colors.accent.teal : theme.colors.neon.blue;

  const inputStyle = {
    width: '100%', padding: '10px 14px', borderRadius: theme.radius.sm,
    border: `1px solid ${theme.colors.borders}`, fontSize: '14px',
    color: theme.colors.text.primary, background: theme.colors.bg.card,
    outline: 'none', boxSizing: 'border-box'
  };

  const labelStyle = {
    display: 'block', fontSize: '13px', fontWeight: 600,
    color: theme.colors.text.secondary, marginBottom: '6px'
  };

  return (
    <div data-testid="nft-project-detail-page" style={{ padding: '40px', maxWidth: '900px' }}>
      <button
        data-testid="back-to-projects-btn"
        onClick={() => navigate('/creator/nft/projects')}
        style={{
          display: 'flex', alignItems: 'center', gap: '6px',
          background: 'none', border: 'none', cursor: 'pointer',
          color: theme.colors.text.secondary, fontSize: '14px', marginBottom: '24px', padding: 0
        }}
      >
        <ArrowLeft size={16} /> Back to Projects
      </button>

      {/* Project Header */}
      <div style={{
        background: theme.colors.bg.card, borderRadius: theme.radius.card,
        border: `1px solid ${theme.colors.borders}`, padding: '28px',
        marginBottom: '24px', boxShadow: theme.colors.shadows.sm
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '16px' }}>
          <div style={{
            width: '48px', height: '48px', borderRadius: '14px',
            background: `${stdColor}10`, display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <Package size={24} style={{ color: stdColor }} />
          </div>
          <div>
            <h1 data-testid="project-detail-title" style={{
              fontFamily: theme.fonts.heading, fontSize: '22px', fontWeight: 700,
              color: theme.colors.text.primary
            }}>
              {project.name}
            </h1>
            <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
              <span style={{
                padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 600,
                background: `${stdColor}12`, color: stdColor
              }}>{project.standard}</span>
              <span style={{ fontSize: '12px', color: theme.colors.text.muted }}>{project.chain}</span>
            </div>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '13px' }}>
          <div>
            <span style={{ color: theme.colors.text.muted }}>Contract: </span>
            <code style={{ color: theme.colors.text.primary, fontSize: '12px' }}>{project.contract_address}</code>
          </div>
          <div>
            <span style={{ color: theme.colors.text.muted }}>Quantity: </span>
            <span style={{ color: theme.colors.text.primary, fontWeight: 600 }}>{project.quantity}</span>
          </div>
        </div>
      </div>

      {/* Generate Items Section */}
      <div style={{
        background: theme.colors.bg.card, borderRadius: theme.radius.card,
        border: `1px solid ${theme.colors.borders}`, padding: '28px',
        marginBottom: '24px', boxShadow: theme.colors.shadows.sm
      }}>
        <h2 style={{ fontSize: '17px', fontWeight: 700, color: theme.colors.text.primary, marginBottom: '20px' }}>
          Generate Metadata Items
        </h2>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div>
              <label style={labelStyle}>Base Name</label>
              <input
                data-testid="gen-base-name"
                type="text" value={genForm.base_name}
                onChange={(e) => setGenForm(p => ({ ...p, base_name: e.target.value }))}
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>Quantity</label>
              <input
                data-testid="gen-quantity"
                type="number" value={genForm.quantity} min={1} max={10000}
                onChange={(e) => setGenForm(p => ({ ...p, quantity: e.target.value }))}
                style={inputStyle}
              />
            </div>
          </div>

          <div>
            <label style={labelStyle}>Description</label>
            <textarea
              data-testid="gen-description"
              value={genForm.description}
              onChange={(e) => setGenForm(p => ({ ...p, description: e.target.value }))}
              rows={2} style={{ ...inputStyle, resize: 'vertical' }}
            />
          </div>

          <div>
            <label style={labelStyle}>Image Base URL (optional)</label>
            <input
              data-testid="gen-image-url"
              type="text" value={genForm.image_base_url}
              onChange={(e) => setGenForm(p => ({ ...p, image_base_url: e.target.value }))}
              placeholder="e.g. ipfs://QmXyz123 or https://mysite.com/images"
              style={inputStyle}
            />
          </div>

          {/* Attributes Schema */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
              <label style={{ ...labelStyle, marginBottom: 0 }}>Attributes (traits)</label>
              <button
                data-testid="add-attribute-btn"
                type="button" onClick={handleAddAttribute}
                style={{
                  display: 'flex', alignItems: 'center', gap: '4px',
                  padding: '4px 12px', borderRadius: theme.radius.sm,
                  background: `${theme.colors.primary}10`, color: theme.colors.primary,
                  border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: 600
                }}
              >
                <Plus size={14} /> Add Trait
              </button>
            </div>

            {genForm.attributes_schema.length === 0 && (
              <p style={{ fontSize: '12px', color: theme.colors.text.muted }}>
                No attributes defined. Add traits like "Background", "Hat", "Eyes" with comma-separated possible values.
              </p>
            )}

            {genForm.attributes_schema.map((attr, idx) => (
              <div key={idx} data-testid={`attribute-row-${idx}`} style={{
                display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '8px'
              }}>
                <input
                  data-testid={`attr-trait-${idx}`}
                  type="text" placeholder="Trait name (e.g. Background)"
                  value={attr.trait_type}
                  onChange={(e) => handleAttrChange(idx, 'trait_type', e.target.value)}
                  style={{ ...inputStyle, flex: 1 }}
                />
                <input
                  data-testid={`attr-values-${idx}`}
                  type="text" placeholder="Values (comma separated)"
                  value={attr.values}
                  onChange={(e) => handleAttrChange(idx, 'values', e.target.value)}
                  style={{ ...inputStyle, flex: 2 }}
                />
                <button
                  type="button" onClick={() => handleRemoveAttribute(idx)}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: theme.colors.status.error, padding: '4px', flexShrink: 0
                  }}
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>

          {genError && (
            <div data-testid="gen-error" style={{
              padding: '10px 14px', borderRadius: theme.radius.sm,
              background: `${theme.colors.status.error}10`, color: theme.colors.status.error,
              fontSize: '13px'
            }}>
              {genError}
            </div>
          )}

          <button
            data-testid="generate-items-btn"
            onClick={handleGenerate}
            disabled={generating}
            style={{
              display: 'flex', alignItems: 'center', gap: '8px', width: 'fit-content',
              padding: '10px 24px', borderRadius: theme.radius.cta,
              background: generating ? theme.colors.text.muted : theme.colors.primary,
              color: '#fff', border: 'none', cursor: generating ? 'not-allowed' : 'pointer',
              fontSize: '14px', fontWeight: 600
            }}
          >
            <RefreshCw size={16} className={generating ? 'animate-spin' : ''} />
            {generating ? 'Generating...' : items.length > 0 ? 'Regenerate Items' : 'Generate Items'}
          </button>
        </div>
      </div>

      {/* P7.1A: IPFS Base URI Section */}
      <div style={{
        background: theme.colors.bg.card, borderRadius: theme.radius.card,
        border: `1px solid ${theme.colors.borders}`, padding: '28px',
        marginBottom: '24px', boxShadow: theme.colors.shadows.sm
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
          <Link2 size={20} style={{ color: theme.colors.primary }} />
          <h2 style={{ fontSize: '17px', fontWeight: 700, color: theme.colors.text.primary }}>
            IPFS Base URI
          </h2>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '14px' }}>
            <div>
              <label style={labelStyle}>IPFS CID (Qm... or bafy...)</label>
              <input
                data-testid="ipfs-cid-input"
                type="text"
                value={ipfsForm.ipfs_cid}
                onChange={(e) => setIpfsForm(p => ({ ...p, ipfs_cid: e.target.value }))}
                placeholder="QmXyz... or bafybeig..."
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>Folder Path</label>
              <input
                data-testid="ipfs-folder-input"
                type="text"
                value={ipfsForm.folder_path}
                onChange={(e) => setIpfsForm(p => ({ ...p, folder_path: e.target.value }))}
                placeholder="metadata"
                style={inputStyle}
              />
            </div>
          </div>

          {computedBaseUri && (
            <div data-testid="computed-base-uri" style={{
              padding: '12px 16px', borderRadius: theme.radius.sm,
              background: theme.colors.bg.tertiary, fontSize: '13px'
            }}>
              <span style={{ color: theme.colors.text.muted, fontWeight: 600 }}>Computed Base URI: </span>
              <code style={{ color: theme.colors.primary, wordBreak: 'break-all' }}>{computedBaseUri}</code>
            </div>
          )}

          {computedBaseUri && (
            <div data-testid="uri-preview-list" style={{
              padding: '12px 16px', borderRadius: theme.radius.sm,
              background: theme.colors.bg.secondary, fontSize: '12px',
              border: `1px solid ${theme.colors.borders}`
            }}>
              <div style={{ fontWeight: 600, color: theme.colors.text.secondary, marginBottom: '6px' }}>Sample URIs:</div>
              {[1, 2, 3].map(n => (
                <div key={n} style={{ color: theme.colors.text.muted, fontFamily: 'monospace', marginBottom: '2px' }}>
                  token {n} → <span style={{ color: theme.colors.text.primary }}>{computedBaseUri}{n}.json</span>
                </div>
              ))}
            </div>
          )}

          {project.base_uri && (
            <div data-testid="saved-base-uri" style={{
              padding: '10px 14px', borderRadius: theme.radius.sm,
              background: `${theme.colors.status.success}10`, fontSize: '13px',
              color: theme.colors.status.success
            }}>
              Saved: <code>{project.base_uri}</code>
            </div>
          )}

          {ipfsError && (
            <div data-testid="ipfs-error" style={{
              padding: '10px 14px', borderRadius: theme.radius.sm,
              background: `${theme.colors.status.error}10`, color: theme.colors.status.error,
              fontSize: '13px'
            }}>
              {ipfsError}
            </div>
          )}

          <button
            data-testid="save-ipfs-btn"
            onClick={handleSaveIpfs}
            disabled={ipfsSaving || !ipfsForm.ipfs_cid}
            style={{
              display: 'flex', alignItems: 'center', gap: '8px', width: 'fit-content',
              padding: '10px 24px', borderRadius: theme.radius.cta,
              background: ipfsSaved ? theme.colors.status.success : ipfsSaving || !ipfsForm.ipfs_cid ? theme.colors.text.muted : theme.colors.primary,
              color: '#fff', border: 'none', cursor: ipfsSaving || !ipfsForm.ipfs_cid ? 'not-allowed' : 'pointer',
              fontSize: '14px', fontWeight: 600
            }}
          >
            {ipfsSaved ? <><Check size={16} /> Saved</> : <><Save size={16} /> {ipfsSaving ? 'Saving...' : 'Save IPFS CID'}</>}
          </button>
        </div>
      </div>

      {/* P7.3: Mint Status Panel */}
      <div style={{
        background: theme.colors.bg.card, borderRadius: theme.radius.card,
        border: `1px solid ${theme.colors.borders}`, padding: '28px',
        marginBottom: '24px', boxShadow: theme.colors.shadows.sm
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
          <ClipboardCheck size={20} style={{ color: MINT_STATUS_COLORS[project.mint_status] || theme.colors.text.muted }} />
          <h2 style={{ fontSize: '17px', fontWeight: 700, color: theme.colors.text.primary }}>Mint Status</h2>
          {project.mint_status && project.mint_status !== 'NOT_MINTED' && (
            <span data-testid="mint-status-badge" style={{
              padding: '2px 10px', borderRadius: '10px', fontSize: '11px', fontWeight: 600,
              background: `${MINT_STATUS_COLORS[project.mint_status]}15`,
              color: MINT_STATUS_COLORS[project.mint_status]
            }}>{project.mint_status}</span>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
            <div>
              <label style={labelStyle}>Status</label>
              <select
                data-testid="mint-status-select"
                value={mintForm.mint_status}
                onChange={(e) => setMintForm(p => ({ ...p, mint_status: e.target.value }))}
                style={{ ...inputStyle, cursor: 'pointer' }}
              >
                <option value="NOT_MINTED">NOT_MINTED</option>
                <option value="PARTIAL">PARTIAL</option>
                <option value="MINTED">MINTED</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>Minted Count (0–{project.quantity})</label>
              <input
                data-testid="minted-count-input"
                type="number" min={0} max={project.quantity}
                value={mintForm.minted_count}
                onChange={(e) => setMintForm(p => ({ ...p, minted_count: e.target.value }))}
                style={inputStyle}
              />
            </div>
          </div>

          <div>
            <label style={labelStyle}>Recipient Address (optional)</label>
            <input
              data-testid="recipient-address-input"
              type="text" value={mintForm.recipient_address}
              onChange={(e) => setMintForm(p => ({ ...p, recipient_address: e.target.value }))}
              placeholder="0x..."
              style={inputStyle}
            />
          </div>

          <div>
            <label style={labelStyle}>Batch Tx Hash (optional)</label>
            <input
              data-testid="batch-tx-hash-input"
              type="text" value={mintForm.batch_tx_hash}
              onChange={(e) => setMintForm(p => ({ ...p, batch_tx_hash: e.target.value }))}
              placeholder="0x..."
              style={inputStyle}
            />
          </div>

          <div>
            <label style={labelStyle}>Notes (optional)</label>
            <textarea
              data-testid="mint-notes-input"
              value={mintForm.notes}
              onChange={(e) => setMintForm(p => ({ ...p, notes: e.target.value }))}
              placeholder="e.g. Minted via Hardhat on 2026-03-30"
              rows={2} style={{ ...inputStyle, resize: 'vertical' }}
            />
          </div>

          {project.mint_confirmed_at && (
            <div data-testid="mint-confirmed-at" style={{
              fontSize: '12px', color: theme.colors.text.muted
            }}>
              Last confirmed: {new Date(project.mint_confirmed_at).toLocaleString()}
            </div>
          )}

          {mintError && (
            <div data-testid="mint-status-error" style={{
              padding: '10px 14px', borderRadius: theme.radius.sm,
              background: `${theme.colors.status.error}10`, color: theme.colors.status.error,
              fontSize: '13px'
            }}>
              {mintError}
            </div>
          )}

          <button
            data-testid="save-mint-status-btn"
            onClick={handleSaveMintStatus}
            disabled={mintSaving}
            style={{
              display: 'flex', alignItems: 'center', gap: '8px', width: 'fit-content',
              padding: '10px 24px', borderRadius: theme.radius.cta,
              background: mintSaved ? theme.colors.status.success : mintSaving ? theme.colors.text.muted : theme.colors.primary,
              color: '#fff', border: 'none', cursor: mintSaving ? 'not-allowed' : 'pointer',
              fontSize: '14px', fontWeight: 600
            }}
          >
            {mintSaved ? <><Check size={16} /> Saved</> : <><Save size={16} /> {mintSaving ? 'Saving...' : 'Save Mint Status'}</>}
          </button>
        </div>
      </div>

      {/* Items Preview + Export */}
      {items.length > 0 && (
        <div style={{
          background: theme.colors.bg.card, borderRadius: theme.radius.card,
          border: `1px solid ${theme.colors.borders}`, padding: '28px',
          boxShadow: theme.colors.shadows.sm
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
            <h2 style={{ fontSize: '17px', fontWeight: 700, color: theme.colors.text.primary }}>
              Metadata Items ({items.length})
            </h2>
            <button
              data-testid="export-pack-btn"
              onClick={handleExport}
              disabled={exporting}
              style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                padding: '10px 20px', borderRadius: theme.radius.cta,
                background: exporting ? theme.colors.text.muted : theme.colors.accent.teal,
                color: '#fff', border: 'none', cursor: exporting ? 'not-allowed' : 'pointer',
                fontSize: '14px', fontWeight: 600
              }}
            >
              <Download size={16} />
              {exporting ? 'Exporting...' : 'Export ZIP Pack'}
            </button>
          </div>
          <p data-testid="export-includes-note" style={{ fontSize: '12px', color: theme.colors.text.muted, marginBottom: '16px' }}>
            Includes metadata JSON, manifest CSV, mint instructions, and Hardhat mint scripts.
          </p>

          {/* Items Table */}
          <div style={{ overflowX: 'auto' }}>
            <table data-testid="items-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${theme.colors.borders}` }}>
                  <th style={{ textAlign: 'left', padding: '10px 12px', color: theme.colors.text.muted, fontWeight: 600 }}>Token ID</th>
                  <th style={{ textAlign: 'left', padding: '10px 12px', color: theme.colors.text.muted, fontWeight: 600 }}>Name</th>
                  <th style={{ textAlign: 'left', padding: '10px 12px', color: theme.colors.text.muted, fontWeight: 600 }}>Image</th>
                  <th style={{ textAlign: 'left', padding: '10px 12px', color: theme.colors.text.muted, fontWeight: 600 }}>Attributes</th>
                </tr>
              </thead>
              <tbody>
                {items.slice(0, 50).map((item) => (
                  <tr key={item.id} data-testid={`item-row-${item.token_id}`} style={{ borderBottom: `1px solid ${theme.colors.borders}` }}>
                    <td style={{ padding: '10px 12px', fontWeight: 600 }}>#{item.token_id}</td>
                    <td style={{ padding: '10px 12px', color: theme.colors.text.primary }}>{item.name}</td>
                    <td style={{ padding: '10px 12px', color: theme.colors.text.muted, fontSize: '12px' }}>{item.image}</td>
                    <td style={{ padding: '10px 12px' }}>
                      <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                        {(item.attributes || []).map((a, i) => (
                          <span key={i} style={{
                            padding: '2px 6px', borderRadius: '6px', fontSize: '11px',
                            background: theme.colors.bg.tertiary, color: theme.colors.text.secondary
                          }}>
                            {a.trait_type}: {a.value}
                          </span>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {items.length > 50 && (
              <p style={{ textAlign: 'center', color: theme.colors.text.muted, fontSize: '12px', padding: '12px' }}>
                Showing first 50 of {items.length} items. All items included in the export.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NftProjectDetail;
